import * as amplitude from '@amplitude/unified';
import { Identify } from '@amplitude/analytics-core';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';

/**
 * Initialize Amplitude analytics
 * Only initializes in production builds
 * Call this once at app startup before rendering
 */
export const initializeAmplitude = () => {
  // Only initialize Amplitude in production builds
  if (import.meta.env.MODE !== 'production') {
    return;
  }

  const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;

  if (!amplitudeApiKey) {
    return;
  }

  amplitude.initAll(amplitudeApiKey, {
    analytics: {
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: true,
      },
    },
  });
};

/**
 * Set the Amplitude user ID and user properties from the authenticated user's ID token
 * Call this after the user has authenticated
 */
export const setAmplitudeUserId = async () => {
  // Only set user ID in production builds
  if (import.meta.env.MODE !== 'production') {
    return;
  }

  try {
    const idTokenClaims = await StandaloneAuth.client.getIdTokenClaims();
    const userId = idTokenClaims?.['https://el/user_id'] as string | undefined;

    if (userId) {
      amplitude.setUserId(userId);

      // Extract email domain for company identification
      const email = idTokenClaims?.email;
      if (email) {
        const emailParts = email.split('@');
        if (emailParts.length === 2 && emailParts[1]) {
          const identify = new Identify();
          identify.set('email_domain', emailParts[1]);
          amplitude.identify(identify);
        }
      }
    }
  } catch (err) {
    // Silently fail if we can't get user ID - user might not be authenticated yet
  }
};
